import {
  useToast,
  useDisclosure,
  Box,
  Flex,
  Divider,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import {
  CompositionGroup,
  compositionGroupApi,
} from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { RenameGroupModal } from '@texas/components/shared/compositionGroup/RenameGroupModal';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { FileBrowserModal } from '@texas/components/shared/files/FileBrowserModal';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ServerError } from '@texas/types';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '@texas/utils/helpers/httpHelpers';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { TFunction } from 'i18next';
import { imageExtensions } from '@texas/utils/helpers/filesHelper';
import { CompositionListModal } from '@texas/components/article/variant/compositions/CompositionListModal';
import { DocumentFileModal } from '@texas/components/article/variant/compositions/shared/file/DocumentFileModal';
import { ConfirmCopy } from '@texas/components/article/variant/compositions/shared/group/ConfirmCopy';
import { CompositionGroupHistoryModal } from '@texas/components/article/variant/history/CompositionGroupHistoryModal';
import { LayoutDropzone } from '@texas/components/article/variant/options/shared/LayoutDropZone';
import { MarkReady } from '@texas/components/article/variant/options/shared/MarkReady';
import { OptionsComponent } from '@texas/components/article/variant/options/shared/OptionsComponent';
import { TemplatePricing } from './TemplatePricing';
import { TemplateConfig } from './TemplateConfig';

export function TemplateCompositionGroupContainer({
  option,
  onArchive,
  onCopy,
}: {
  option: CompositionGroup;
  onArchive?: () => Promise<void>;
  onCopy?: (fromId: number) => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation();

  const {
    isOpen: isArchivedCompositionsOpen,
    onClose: onArchivedCompositionsClose,
    onOpen: onArchivedCompositionsOpen,
  } = useDisclosure();

  const {
    isOpen: isFileBrowserOpen,
    onClose: onFileBrowserClose,
    onOpen: onFileBrowserOpen,
  } = useDisclosure();

  const {
    isOpen: isFileModalOpen,
    onClose: onFileModalClose,
    onOpen: onfileModalOpen,
  } = useDisclosure();

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();
  const { request: updateRequest } = useApiRequest(
    compositionGroupApi.updateFile,
  );

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [option.id, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          compositionGroupEvents.onLayoutChanged.dispatch();
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [option.id, t, toast, updateRequest],
  );

  const { request: markNotReadyReqest, loading: notReadyLoading } =
    useApiRequest(compositionGroupApi.markNotReady);
  const { request: markReadyRequest, loading: readyLoading } = useApiRequest(
    compositionGroupApi.markReady,
  );

  const markNotReady = async () =>
    await request(
      markNotReadyReqest,
      [option.id],
      () => {
        toast({
          title: t('compositionGroup.option.markedNotReady'),
          status: 'success',
          isClosable: true,
        });
        compositionGroupEvents.onMarkNotReady.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  const markReady = async () =>
    await request(
      markReadyRequest,
      [option.id],
      () => {
        toast({
          title: t('compositionGroup.option.markedReady'),
          status: 'success',
          isClosable: true,
        });
        compositionGroupEvents.onMarkReady.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile);

  const {
    isOpen: isEditGroupOpen,
    onClose: onEditGroupClose,
    onOpen: onEditGroupOpen,
    value: editGroupValue,
  } = useValueDisclosure<{ id: number; note: string | null }>();
  const {
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useDisclosure();
  const {
    isOpen: isCopyOpen,
    onClose: onCopyClose,
    onOpen: onCopyOpen,
    value: copyValue,
  } = useValueDisclosure<{
    version: number;
    id: number;
  }>();
  const {
    isOpen: isHistoryOpen,
    onClose: onHistoryClose,
    onOpen: onHistoryOpen,
    value: historyValue,
  } = useValueDisclosure<{
    id: number;
    name: string;
  }>();

  const ref = useRef<HTMLDivElement>(null);

  return (
    <Box w="full" overflow="hidden" ref={ref}>
      <Flex role="group" flexDir="column">
        <CompositionGroupTitle
          version={option.version}
          note={option.note}
          type="option"
        />
        <Flex gap={4} flexWrap="wrap">
          <Flex gap={4}>
            <LayoutDropzone
              onUpload={(files) => uploadFiles(files, option.folderId)}
              onfileModalOpen={onfileModalOpen}
              fileId={option.fileId}
            />
            <MarkReady
              isLoading={readyLoading || notReadyLoading}
              isReady={option.isReady}
              hasAnyBasedOn={option.hasAnyBasedOn}
              onClick={markReady}
              containerWarningRef={ref}
            />
          </Flex>
          <Flex
            gap={2}
            align="center"
            transition="opacity 200ms ease, transform 200ms ease"
            flexWrap="wrap"
            opacity={0}
            transform="translateX(0)"
            _groupHover={{ opacity: 1, transform: 'translateX(22px)' }}
            pb={1}
          >
            {option.isReady && (
              <Tooltip label={t('compositionGroup.option.markNotReady')}>
                <IconButton
                  onClick={markNotReady}
                  icon={<Icons.Undo boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  aria-label={t('compositionGroup.option.markNotReady')}
                />
              </Tooltip>
            )}
            <Tooltip label={t('general.rename')}>
              <IconButton
                onClick={() =>
                  onEditGroupOpen({ id: option.id, note: option.note })
                }
                icon={<Icons.NoteEdit boxSize={defaultIconSize} />}
                variant="no-bg-dark"
                aria-label={t('general.rename')}
              />
            </Tooltip>
            <Tooltip label={t('composition.archivedCompositions')}>
              <IconButton
                onClick={onArchivedCompositionsOpen}
                icon={<Icons.LayersOutline boxSize={defaultIconSize} />}
                variant="no-bg-dark"
                aria-label={t('composition.archivedCompositions')}
              />
            </Tooltip>
            {onCopy && (
              <Tooltip label={t('composition.copyIntoNewOption')}>
                <IconButton
                  onClick={() =>
                    onCopyOpen({ version: option.version, id: option.id })
                  }
                  icon={<Icons.ContentCopy boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  aria-label={t('composition.copyIntoNewOption')}
                />
              </Tooltip>
            )}

            <Tooltip label={t('history.viewHistory')}>
              <IconButton
                onClick={() =>
                  onHistoryOpen({
                    id: option.id,
                    name: option.note
                      ? t('compositionGroup.option.optionNrNote', {
                          note: option.note,
                          nr: option.version,
                        })
                      : t('compositionGroup.option.optionNr', {
                          nr: option.version,
                        }),
                  })
                }
                variant="no-bg-dark"
                icon={<Icons.Clock boxSize={defaultIconSize} />}
                aria-label={t('history.viewHistory')}
              />
            </Tooltip>
            {onArchive && (
              <Tooltip label={t('compositionGroup.option.archive')}>
                <IconButton
                  onClick={onArchiveOpen}
                  icon={<Icons.ArchiveOutline boxSize={defaultIconSize} />}
                  variant="no-bg-dark"
                  color="red.200"
                  _light={{ color: 'red.400' }}
                  aria-label={t('compositionGroup.option.archive')}
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Divider py={2} />
      <Tabs w="full" variant="composition" pt={4}>
        <TabList gap={4}>
          <WarningComponent
            isMandatory={true}
            warning={compositionTabWarning(option, t)}
          >
            <Tab h="full">
              <Flex gap={1} align="center">
                <Icons.Layers /> <Text>{t('composition.compositions')}</Text>
              </Flex>
            </Tab>
          </WarningComponent>
          <Tab h="full">
            <Flex gap={1} align="center">
              <Icons.Cog /> <Text>{t('configuration.templateConfig')}</Text>
            </Flex>
          </Tab>
          <Tab h="full">
            <Flex gap={1} align="center">
              <Icons.CashMultiple /> <Text>{t('general.pricing')}</Text>
            </Flex>
          </Tab>
        </TabList>
        <TabPanels pt="6">
          <TabPanel p={0}>
            <OptionsComponent isVdc={false} isMatrix={false} option={option} />
          </TabPanel>
          <TabPanel p={0} pb={8}>
            <TemplateConfig compositionGroupId={option.id} />
          </TabPanel>
          <TabPanel>
            <TemplatePricing
              compositionGroupPurchaseInfoId={
                option.compositionGroupPurchaseInfoId!
              }
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <DocumentFileModal
        fileId={option.fileId}
        onRemoveFileOpen={onRemoveFileOpen}
        onFileBrowserOpen={onFileBrowserOpen}
        onUpload={(files) => uploadFiles(files, option.folderId)}
        isUploading={isUploading}
        uploadFailed={uploadFailed}
        fileUploads={fileUploads}
        removeFileUpload={removeFileUpload}
        isOpen={isFileModalOpen}
        onClose={onFileModalClose}
      />
      <FileBrowserModal
        rootFolderId={option.folderId}
        mode="Select"
        isOpen={isFileBrowserOpen}
        onClose={onFileBrowserClose}
        onSelect={(file: FileLink) => {
          updateFile(file);
          onFileBrowserClose();
        }}
        rejectExtensionFilter={imageExtensions}
      />
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeLayout')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={compositionGroupApi.deleteFile}
        args={[option.id]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => compositionGroupEvents.onLayoutChanged.dispatch()}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <RenameGroupModal
        onUpdated={() => compositionGroupEvents.onRename.dispatch()}
        group={editGroupValue}
        onClose={onEditGroupClose}
        isOpen={isEditGroupOpen}
      />
      {onArchive && (
        <VerifyDialogWithFunction
          headerTitle={t('compositionGroup.option.archive')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          toPerform={async () => {
            await onArchive();
            onArchiveClose();
          }}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
        >
          {t('compositionGroup.option.archiveDesc')}
        </VerifyDialogWithFunction>
      )}

      <CompositionListModal
        isOpen={isArchivedCompositionsOpen}
        onClose={onArchivedCompositionsClose}
        compositionGroupId={option.id}
      />
      {copyValue && onCopy && (
        <ConfirmCopy
          title={t('composition.copyIntoNewOption')}
          buttonLabel={t('composition.copyComposition')}
          isOpen={isCopyOpen}
          onClose={onCopyClose}
          compositionGroupId={copyValue.id}
          hideCopyConfig={true}
          onConfirm={(id) => {
            onCopyClose();
            onCopy(id);
          }}
        />
      )}
      {historyValue && (
        <CompositionGroupHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          compositionGroupId={historyValue.id}
          name={historyValue.name}
        />
      )}
    </Box>
  );
}

function compositionTabWarning(option: CompositionGroup, t: TFunction) {
  if (option.compositions.length === 0) {
    return t('compositionGroup.option.missingCompositions');
  }

  if (option.compositions.some((c) => c.materials.length === 0)) {
    return t('compositionGroup.option.missingMaterials');
  }

  if (option.compositions.some((c) => c.dimensionGroup === null)) {
    return t('compositionGroup.option.missingDimensions');
  }

  return undefined;
}
